/**
 * Модульный синглтон для отслеживания активной сессии редактирования процедуры.
 * Работает вне дерева React — доступен из любого компонента или модуля.
 *
 * Жизненный цикл сессии:
 *  1. Пользователь нажимает «Редактировать» → POST /scenarios/{id}/versions/start → editSessionStore.set(...)
 *  2. Пользователь делает правки (создаёт/обновляет/удаляет шаги и пр.)
 *  3. Пользователь выходит из режима редактирования (кнопка «Выйти» / навигация) →
 *       POST /scenarios/{id}/versions/complete → editSessionStore.clear()
 *  4. При закрытии/перезагрузке браузера → beforeunload → completeWithKeepalive()
 */

export interface ActiveEditSession {
  scenarioId: number
  accessToken: string
}

type Listener = (session: ActiveEditSession | null) => void

let _activeSession: ActiveEditSession | null = null
const _listeners = new Set<Listener>()

function notify() {
  _listeners.forEach((l) => l(_activeSession))
}

export const editSessionStore = {
  /** Текущая активная сессия (или null). */
  get(): ActiveEditSession | null {
    return _activeSession
  },

  /** Зарегистрировать активную сессию редактирования. */
  set(session: ActiveEditSession): void {
    _activeSession = session
    notify()
  },

  /** Очистить стор (без API-вызова). Вызывается после успешного complete. */
  clear(): void {
    _activeSession = null
    notify()
  },

  /**
   * Подписаться на изменения сессии.
   * Возвращает функцию отписки.
   */
  subscribe(listener: Listener): () => void {
    _listeners.add(listener)
    return () => _listeners.delete(listener)
  },

  /**
   * Завершить сессию с опцией keepalive=true.
   * Используется в обработчике beforeunload — запрос выполнится даже
   * если страница выгружается.
   *
   * ВАЖНО: стор при этом НЕ очищается, чтобы не сломать состояние
   * если пользователь отменил закрытие страницы (нажал «Остаться»).
   * В этом случае следующая мутация может вернуть ошибку с сервера
   * (сессия уже закрыта) — её нужно обработать на уровне страницы.
   */
  completeWithKeepalive(): void {
    if (!_activeSession) return
    const { scenarioId, accessToken } = _activeSession

    const apiBase = process.env.NEXT_PUBLIC_V3_API_URL
    if (!apiBase) return

    fetch(`${apiBase}/scenarios/${scenarioId}/versions/complete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      keepalive: true,
    }).catch(() => {
      // Игнорируем ошибки keepalive-запроса
    })
  },
}
